/* eslint-disable @typescript-eslint/no-explicit-any */

import { PayloadAction } from '@reduxjs/toolkit';

import { ErrorType } from '../../api/errorHandling';
import { FileType, StructureType } from '../../types/types';

export interface FileStorageItem {
 Created: string;
 DocData: string;
 DocPicType: string;
 Id: number;
 KescId: number;
 Name: string;
 Thumbnail: number[];
 UserName: string;
 Weighings: {
  Id: number;
  Time: string;
  Weight: number;
 };
 Type?: string;
}

export interface CommonState {
 items: any[];
 aggregate: string | null;
 item: any;
 structure: StructureType[];
 documents: FileStorageItem[];
 loading: { [key: string]: boolean | null };
 success: { [key: string]: boolean | null };
 rowCount: string;
 excel: FileType | null;
}

export const initialCommonState: CommonState = {
 items: [],
 aggregate: null,
 item: null,
 structure: [],
 documents: [],
 rowCount: '0',
 loading: {},
 success: {},
 excel: null,
};

export const commonReducers = {
 getTableStart(
  state: any,
  action: PayloadAction<{ table: string; url?: string; data?: any }>,
 ) {
  return {
   ...state,
   loading: { ...state.loading, getTable: true },
  };
 },
 getTableSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   items: action.payload.data,
   aggregate: action.payload.aggregate,
   rowCount: action.payload.rowCount,
   success: { ...state.success, getTable: true },
   loading: { ...state.loading, getTable: null },
  };
 },
 getTableFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, getTable: false },
   loading: { ...state.loading, getTable: null },
  };
 },
 getTableFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, getTable: null },
   loading: { ...state.loading, getTable: null },
  };
 },
 // get item
 getItemStart(
  state: any,
  action: PayloadAction<{ table: string; id: number | string }>,
 ) {
  return {
   ...state,
   loading: { ...state.loading, getItem: true },
  };
 },
 getItemSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   item: action.payload.data,
   success: { ...state.success, getItem: true },
   loading: { ...state.loading, getItem: null },
  };
 },
 getItemFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, getItem: false },
   loading: { ...state.loading, getItem: null },
  };
 },
 getItemFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, getItem: null },
   loading: { ...state.loading, getItem: null },
  };
 },
 // create item
 createItemStart(
  state: any,
  action: PayloadAction<{ table: string; data: any }>,
 ) {
  return {
   ...state,
   loading: { ...state.loading, createItem: true },
  };
 },
 createItemSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   success: { ...state.success, createItem: true },
   loading: { ...state.loading, createItem: null },
  };
 },
 createItemFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, createItem: false },
   loading: { ...state.loading, createItem: null },
  };
 },
 createItemFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, createItem: null },
   loading: { ...state.loading, createItem: null },
  };
 },
 // update item
 updateItemStart(
  state: any,
  action: PayloadAction<{
   table: string;
   id: number | string;
   data: any;
   isPut?: boolean;
  }>,
 ) {
  return {
   ...state,
   loading: { ...state.loading, updateItem: true },
  };
 },
 updateItemSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   table: action.payload.data,
   success: { ...state.success, updateItem: true },
   loading: { ...state.loading, updateItem: null },
  };
 },
 updateItemFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, updateItem: false },
   loading: { ...state.loading, updateItem: null },
  };
 },
 updateItemFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, updateItem: null },
   loading: { ...state.loading, updateItem: null },
  };
 },
 // delete items
 deleteItemsStart(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   loading: { ...state.loading, deleteItems: true },
  };
 },
 deleteItemsSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   table: action.payload.data,
   success: { ...state.success, deleteItems: true },
   loading: { ...state.loading, deleteItems: null },
  };
 },
 deleteItemsFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, deleteItems: false },
   loading: { ...state.loading, deleteItems: null },
  };
 },
 deleteItemsFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, deleteItems: null },
   loading: { ...state.loading, deleteItems: null },
  };
 },
 // delete item
 deleteItemStart(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   loading: { ...state.loading, deleteItem: true },
  };
 },
 deleteItemSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   table: action.payload.data,
   success: { ...state.success, deleteItem: true },
   loading: { ...state.loading, deleteItem: null },
  };
 },
 deleteItemFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, deleteItem: false },
   loading: { ...state.loading, deleteItem: null },
  };
 },
 deleteItemFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, deleteItem: null },
   loading: { ...state.loading, deleteItem: null },
  };
 },

 // get table structure
 getTableStructureStart(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   loading: { ...state.loading, getTableStructure: true },
  };
 },
 getTableStructureSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   structure: action.payload.data,
   success: { ...state.success, getTableStructure: true },
   loading: { ...state.loading, getTableStructure: null },
  };
 },
 getTableStructureFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, getTableStructure: null },
   loading: { ...state.loading, getTableStructure: null },
  };
 },
 getTableStructureFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, getTableStructure: false },
   loading: { ...state.loading, getTableStructure: null },
  };
 },

 // get item documents
 getItemDocumentsStart(state: any, action: PayloadAction<number>) {
  return {
   ...state,
   loading: { ...state.loading, getItemDocuments: true },
  };
 },
 getItemDocumentsSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   documents: action.payload.data,
   success: { ...state.success, getItemDocuments: true },
   loading: { ...state.loading, getItemDocuments: null },
  };
 },
 getItemDocumentsFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, getItemDocuments: false },
   loading: { ...state.loading, getItemDocuments: null },
  };
 },

 // upload item documents
 uploadItemDocumentsStart(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   loading: { ...state.loading, uploadItemDocuments: true },
  };
 },
 uploadItemDocumentsSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   success: { ...state.success, uploadItemDocuments: true },
   loading: { ...state.loading, uploadItemDocuments: null },
  };
 },
 uploadItemDocumentsFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, uploadItemDocuments: false },
   loading: { ...state.loading, uploadItemDocuments: null },
  };
 },
 uploadItemDocumentsFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, uploadItemDocuments: null },
   loading: { ...state.loading, uploadItemDocuments: null },
  };
 },

 // delete item document
 deleteItemDocumentStart(state: any, action: PayloadAction<number>) {
  return {
   ...state,
   loading: { ...state.loading, deleteItemDocument: true },
  };
 },
 deleteItemDocumentSuccess(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   success: { ...state.success, deleteItemDocument: true },
   loading: { ...state.loading, deleteItemDocument: null },
  };
 },
 deleteItemDocumentFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, deleteItemDocument: false },
   loading: { ...state.loading, deleteItemDocument: null },
  };
 },
 deleteItemDocumentFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, deleteItemDocument: null },
   loading: { ...state.loading, deleteItemDocument: null },
  };
 },
 getTableExcelStart(state: any, action: PayloadAction<any>) {
  return {
   ...state,
   loading: { ...state.loading, getTableExcel: true },
  };
 },
 getTableExcelSuccess(state: any, action: PayloadAction<any>) {
  console.log({
   ...state,
   excel: action.payload.excel,
   success: { ...state.success, getTableExcel: true },
   loading: { ...state.loading, getTableExcel: null },
  });
  return {
   ...state,
   excel: action.payload.excel,
   success: { ...state.success, getTableExcel: true },
   loading: { ...state.loading, getTableExcel: null },
  };
 },
 getTableExcelFailure(state: any, action: PayloadAction<ErrorType>) {
  return {
   ...state,
   success: { ...state.success, getTableExcel: false },
   loading: { ...state.loading, getTableExcel: null },
  };
 },
 getTableExcelFinish(state: any) {
  return {
   ...state,
   success: { ...state.success, getTableExcel: null },
   loading: { ...state.loading, getTableExcel: null },
  };
 },
};

